import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { vertexShaderSrc, makeTargetTexture, quadBuffer } from './Canvas';
import { ImageBuffer } from './ImageBuffer';
import { BlankHeader } from './Header';
import { ParsedShaderSet, PropertyKeyframeSet, ShaderId } from './parser/parser';
import { GLSLUniformLocations } from './parser/propTypes';
import { CheckboxInput } from './propComponents';
import { selectStyles } from './styles/selectStyles';
import Select from 'react-select';

// Intervals (in ms) to update user interface
const PROGRESS_UPDATE_INTERVAL = 1000;

// Render options dialog
interface RenderOptionsProps {
  size: [number, number];
  setSize: (size: [number, number]) => void;

  tiling: number;
  setTiling: (tiling: number) => void;

  samples: number;
  setSamples: (samples: number) => void;

  doAnimation: boolean;
  setDoAnimation: (doAnimation: boolean) => void;

  fps: number;
  setFps: (fps: number) => void;

  animateRange: [number, number];
  setAnimateRange: (animateRange: [number, number]) => void;

  motionBlur: boolean;
  setMotionBlur: (motionBlur: boolean) => void;

  previewInterval: number;
  setPreviewInterval: (previewInterval: number) => void;

  beginRender: () => void;
  stopRender: () => void;
}

interface ResolutionOption {
  label: string;
  value: [number, number];
}

interface ResolutionInputProps {
  size: [number, number];
  setSize: (size: [number, number]) => void;
  separateLabel: string;
  defaultOptions?: ResolutionOption[];
}

function ResolutionInput(props: ResolutionInputProps) {
  const { size, setSize, separateLabel, defaultOptions } = props;
  const [input, setInput] = useState<[string, string]>([size[0].toString(), size[1].toString()]);
  const [selectedDefault, setSelectedDefault] = useState<number | null>(null);

  return (
    <>
      <input 
        className="shortInput" type="text" value={input[0]}
        onChange={event => {
          setInput([event.target.value, input[1]]);
          setSize([+event.target.value, size[1]]);
          setSelectedDefault(null);
        }}
      />
      {separateLabel}
      <input 
        className="shortInput" type="text" value={input[1]}
        onChange={event => {
          setInput([input[0], event.target.value]);
          setSize([size[0], +event.target.value]);
          setSelectedDefault(null);
        }}
      />
      {defaultOptions !== undefined && 
        <Select 
          options={defaultOptions}
          value={selectedDefault === null ? null : defaultOptions[selectedDefault]}
          onChange={(option) => {
            if(option === null) return;

            setSelectedDefault(defaultOptions.findIndex(opt => opt.label === option.label));
            setInput([option.value[0].toString(), option.value[1].toString()]);
            setSize(option.value);
          }}
          styles={selectStyles}
          className="selectComponent inlineSelect"
        />
      }
    </>
  );
}

// default resolution options
const defaultResolutions: ResolutionOption[] = [
  { label: "240p (320 x 240)", value: [320, 240] },
  { label: "480p (640 x 480)", value: [640, 480] },
  { label: "720p (1280 x 720)", value: [1280, 720] },
  { label: "1080p (1920 x 1080)", value: [1920, 1080] },
  { label: "1440p (2560 x 1440)", value: [2560, 1440] },
  { label: "4K (3840 x 2160)", value: [3840, 2160] },
  { label: "8K (7680 x 4320)", value: [7680, 4320] }
];

// display update rate options
const updateFreqOptions = [
  { label: "Only Finished Frames", value: -1 },
  { label: "1 Second", value: 1 * 1000 },
  { label: "5 Seconds", value: 5 * 1000 },
  { label: "20 Seconds", value: 20 * 1000 },
  { label: "1 Minute", value: 60 * 1000 },
  { label: "5 Minutes", value: 5 * 60 * 100 },
];

function RenderOptions(props: RenderOptionsProps) {
  const { size, setSize, tiling, setTiling, samples, setSamples, doAnimation, setDoAnimation, fps, setFps, animateRange, setAnimateRange, motionBlur, setMotionBlur, previewInterval, setPreviewInterval, beginRender, stopRender } = props;
  // User specified size -- this is converted to be a multiple of tiling
  const [inputSize, setInputSize] = useState(size);
  // Inputted samples
  const [inputSamples, setInputSamples] = useState(samples.toString());
  const [inputFps, setInputFps] = useState(fps.toString());

  const setSizeAndTiling = (newSize: [number, number], tiling: number) => {
    setSize([Math.floor(newSize[0] / tiling) * tiling, Math.floor(newSize[1] / tiling) * tiling]);
    setTiling(tiling);
  }

  return (
    <div className="renderOptions">
      <div className="renderOptionsTitle">Rendering Parameters</div>
      <div className="renderOption">
        <label className="noMargin">
          Resolution: <br/>
        </label>
        <ResolutionInput
          size={inputSize}
          setSize={newSize => {
            setInputSize(newSize);
            setSizeAndTiling(newSize, tiling);
          }}
          separateLabel="x"
          defaultOptions={defaultResolutions}
        />
      </div>

      <label>
        Tiling: {`${tiling * tiling} Tiles (${tiling} x ${tiling})`}<br/>
        <input 
          type="range" min="1" max="16" step="1"
          value={tiling}
          onChange={event => setSizeAndTiling(inputSize, +event.target.value)}
        />
      </label>
      <label>
        Samples Per Pixel: <br />
        <input 
          type="text" value={inputSamples}
          onChange={event => {
            setInputSamples(event.target.value);
            setSamples(+event.target.value)
          }}
        />
      </label>
      <CheckboxInput
        label="Enable Motion Blur"
        checked={motionBlur} 
        onChange={checked => setMotionBlur(checked)}
      />
      <CheckboxInput
        label="Enable Animation"
        checked={doAnimation} 
        onChange={checked => setDoAnimation(checked)}
      />
      {doAnimation && <>
      <label>
        Frames Per Second: <br />
        <input
          type="text" value={inputFps}
          onChange={event => {
            setInputFps(event.target.value);
            setFps(+event.target.value);
          }}
        />
      </label>
      <label>
        Section of Animation to Render (s): <br />
        <ResolutionInput
          size={animateRange}
          setSize={newRange => {
            setAnimateRange(newRange)
          }}
          separateLabel="thru"
        />
      </label>
      </>}
      <hr/>
      <label>
       Render Preview Update Interval:
      <Select
        options={updateFreqOptions}
        value={updateFreqOptions.find(opt => opt.value === previewInterval)}
        onChange={(option) => {
          if(option !== null) {
            setPreviewInterval(option.value);
          }
        }}
        styles={selectStyles}
        className="selectComponent inlineSelect"
      />
      </label>
      <hr/>
      <div className="flex">
        <button className="flexButton" onClick={beginRender}>Begin Rendering</button>
        <button className="flexButton" onClick={stopRender}>Cancel Rendering</button>
      </div>
    </div>
  );
}

interface RenderProps {
  parsedShaders: ParsedShaderSet;
  activeShaders: ShaderId[];
  shaderProps: PropertyKeyframeSet;
  maxTime: number;

  onClose: () => void;
  onExitRender: () => void;
}

// Create a webgl context for the given size and tiling + bind framebuffer with float rgba texture for rendering
function getWebGLContext(size: [number, number], tiling: number): WebGL2RenderingContext | null {
  const canvas = document.createElement("canvas");
  canvas.width = size[0] / tiling;
  canvas.height = size[1] / tiling;
  const gl = canvas.getContext("webgl2");
  if(gl === null) {
    alert("Cannot get WebGL 2 context");
    return null;
  }
  // create framebuffer
  const fb = makeTargetTexture([size[0] / tiling, size[1] / tiling], gl)[1];
  gl.bindFramebuffer(gl.FRAMEBUFFER, fb);

  gl.clearColor(0.0, 0.0, 0.0, 1.0);
  gl.clear(gl.COLOR_BUFFER_BIT);
  gl.viewport(0, 0, size[0] / tiling, size[1] / tiling);

  return gl;
}

// Compile a WebGL shader
function compileShader(type: number, src: string, gl: WebGL2RenderingContext): WebGLShader | null {
  const shader = gl.createShader(type);
  if(shader === null) {
    alert("Cannot create shader.");
    return null;
  }
  gl.shaderSource(shader, src);
  gl.compileShader(shader);
  if(!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    alert("Failed to compile shader:\n" + gl.getShaderInfoLog(shader));
    return null;
  }

  return shader;
}

// Compile vertex + fragment shaders
function compileShaders(gl: WebGL2RenderingContext, parsedShaders: ParsedShaderSet, activeShaders: ShaderId[], shaderProps: PropertyKeyframeSet, time: number): [WebGLProgram, GLSLUniformLocations] | null {
  const program = gl.createProgram();
  if(program === null) {
    alert("Cannot create WebGL program.");
    return null;
  }

  // Generate fragment shader
  const props = parsedShaders.interpolate(shaderProps, time);
  const fragShaderSrc = parsedShaders.emitGLSL(activeShaders, props);
  // compile + link shaders
  const vertex = compileShader(gl.VERTEX_SHADER, vertexShaderSrc, gl);
  const frag = compileShader(gl.FRAGMENT_SHADER, fragShaderSrc, gl);
  if(vertex === null || frag === null) {
    return null;
  }
  gl.attachShader(program, vertex);
  gl.attachShader(program, frag);
  gl.linkProgram(program);
  if(!gl.getProgramParameter(program, gl.LINK_STATUS)) {
    alert("Failed to link program:\n" + gl.getProgramInfoLog(program));
    return null;
  }
  gl.useProgram(program);
  // find uniform locations
  let uniformLocs: GLSLUniformLocations = {};
  const uniforms = parsedShaders.getUniformNames(activeShaders, props);
  uniforms.forEach((name) => {
    uniformLocs[name] = gl.getUniformLocation(program, name);
  });
  // setup drawing geometry
  quadBuffer(gl);

  return [program, uniformLocs];
}

// Render to webgl context and add to image buffer
function renderTile(gl: WebGL2RenderingContext, uniformLocs: GLSLUniformLocations, size: [number, number], tiling: number, program: WebGLProgram, buffer: ImageBuffer, parsedShaders: ParsedShaderSet, activeShaders: ShaderId[], shaderProps: PropertyKeyframeSet, time: number) {
  // Set preamble uniforms
  gl.uniform2fv(gl.getUniformLocation(program, "u_resolution"), size);
  gl.uniform1i(gl.getUniformLocation(program, "u_progressive_enable"), 1);
  gl.uniform3fv(gl.getUniformLocation(program, "u_progressive"), [...buffer.curTile, tiling]);
  gl.uniform1f(gl.getUniformLocation(program, "u_random"), Math.random());
  gl.uniform1i(gl.getUniformLocation(program, "u_interactive"), 0);
  // Set user specified uniforms
  parsedShaders.setGLSLUniforms(activeShaders, parsedShaders.interpolate(shaderProps, time), gl, uniformLocs);

  // Draw
  const aPositionLoc = gl.getAttribLocation(program, "a_position");
  gl.vertexAttribPointer(aPositionLoc, 2, gl.FLOAT, false, 0, 0);
  gl.enableVertexAttribArray(aPositionLoc);
  gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);

  // Add to buffer
  gl.readPixels(0, 0, size[0] / tiling, size[1] / tiling, gl.RGBA, gl.FLOAT, buffer.tile);
  buffer.addTile();
}

export default function Render(props: RenderProps) {
  const {parsedShaders, activeShaders, shaderProps, maxTime} = props;

  // Resolution of final image to render
  const [size, setSize] = useState<[number, number]>([640, 480]);
  // Tiling factor
  const [tiling, setTiling] = useState<number>(1);
  // Samples per pixel
  const [samples, setSamples] = useState<number>(8);
  // Whether to render animation
  const [doAnimation, setDoAnimation] = useState<boolean>(false);
  // Framerate
  const [fps, setFps] = useState<number>(24);
  // Start and end times of animation
  const [animateRange, setAnimateRange] = useState<[number, number]>([0.0, maxTime]);
  // Motion blur: adjust time across frame samples
  const [motionBlur, setMotionBlur] = useState(true);
  // Progress message to display [msg, overall, frame, sample, tile]
  const [progressMsg, setProgressMsg] = useState<[string, number | null, number | null, number | null, number | null]>(["Not Rendering", null, null, null, null]);
  // Last time info was updated [progress, preview]
  const lastInfo = useRef<[DOMHighResTimeStamp, DOMHighResTimeStamp]>([performance.now(), performance.now()]);
  // How often to update progress
  const [previewInterval, setPreviewInterval] = useState<number>(5000);

  // JSZip instance that rendered images are saved to [root, folder]
  const resultFile = useRef<[JSZip, JSZip] | null>(null);

  // Save result zip
  const saveResultFile = useCallback(() => {
    if(resultFile.current === null) {
      alert("No resultFile to save");
      return;
    }
    resultFile.current[0].generateAsync({type: "blob"}).then(content => {
      saveAs(content, "images.zip");
    });
  }, [resultFile]);

  // Canvas to draw output on
  const drawCanvas = useRef<HTMLCanvasElement | null>(null);
  const drawContext = useRef<CanvasRenderingContext2D | null>(null);
  useEffect(() => {
    if(drawCanvas.current !== null)
      drawContext.current = drawCanvas.current.getContext("2d");
  }, [drawCanvas]);

  // Rendering loop requestAnimationFrame id
  const renderCallbackId = useRef<number | null>(null);

  const totalFrames = doAnimation ? (animateRange[1] - animateRange[0]) * fps : 1;

  // Main rendering loop
  const renderLoop = useCallback((gl: WebGL2RenderingContext, program: WebGLProgram, buffer: ImageBuffer, uniformLocs: GLSLUniformLocations, timeSeconds: number, frameIndex: number) => {
    if(drawContext.current === null || drawCanvas.current === null) return;

    const curSample = buffer.samplesComplete();
    const time = timeSeconds + (frameIndex / fps) + (motionBlur ? (curSample / (samples * fps)) : 0);

    // Check if we've reached the end of the animation
    if(time >= animateRange[1]) {
      setProgressMsg(["Done", 1.0, null, null, null]);
      return;
    }
    // Calculate progress
    const now = performance.now();
    const curFrame = timeSeconds * fps + frameIndex - animateRange[0] * fps;

    if(now - lastInfo.current[0] > PROGRESS_UPDATE_INTERVAL) {
      const [curTile, totalTiles] = buffer.sampleTileProgress();
      const progress = curFrame / totalFrames + (curSample / samples) / totalFrames + (curTile / totalTiles) / (samples * totalFrames);
      setProgressMsg(["Rendering", progress, curFrame + 1, curSample + 1, curTile + 1]);
      lastInfo.current[0] = now;
    }

    // Render the tile
    renderTile(gl, uniformLocs, size, tiling, program, buffer, parsedShaders, activeShaders, shaderProps, time);
    // If we've rendered all of the samples, advance to next frame
    if(buffer.samplesComplete() >= samples) {
      // Advance frame
      frameIndex++;
      if(frameIndex >= fps) {
        frameIndex = 0;
        timeSeconds++;
      }
      const newTime = timeSeconds + (frameIndex / fps);

      // draw to canvas
      buffer.draw(drawContext.current);
      buffer.clear();
      lastInfo.current[1] = performance.now();
      // get image data blob
      drawCanvas.current.toBlob((blob) => {
        if(blob === null) {
          alert("Couldn't generate image");
          return;
        }
        // If we aren't animating, save image directly
        if(!doAnimation) saveAs(blob, "image.png");

        // Otherwise, add to zip file
        if(resultFile.current !== null) {
          resultFile.current[1].file(`frame-${curFrame}.png`, blob);
        }

        // If we reached the end of animation, save images
        if(doAnimation && newTime >= animateRange[1]) {
          saveResultFile();
        }
      });
      
      // If we aren't animating, we're done
      if(!doAnimation) {
        setProgressMsg(["Done", 1.0, null, null, null]);
        return;
      }
    } else {
      // check if we should update the canvas
      if(previewInterval >= 0 && now - lastInfo.current[1] > previewInterval) {
        buffer.draw(drawContext.current);
        lastInfo.current[1] = now;
      }
    }

    // Trigger next render loop
    renderCallbackId.current = window.setTimeout(() => renderLoop(gl, program, buffer, uniformLocs, timeSeconds, frameIndex), 0);
  }, [size, tiling, parsedShaders, activeShaders, shaderProps, animateRange, doAnimation, fps, samples, totalFrames, motionBlur, previewInterval, saveResultFile]);

  // Start rendering
  const beginRender = useCallback(() => {
    // Setup WebGL
    const gl = getWebGLContext(size, tiling);
    if(gl === null) return;
    const compileRes = compileShaders(gl, parsedShaders, activeShaders, shaderProps, animateRange[0]);
    if(compileRes === null) return;
    const [program, uniformLocs] = compileRes;
    const buffer = new ImageBuffer(size, tiling);

    // Calculate start time
    const timeSeconds = Math.floor(animateRange[0]);
    const frameIndex = (animateRange[0] % 1.0) * fps;

    // Create JSZip instance for results
    const zip = new JSZip();
    const folder = zip.folder("images");
    if(folder === null) throw new Error("Can't create folder in zip");
    resultFile.current = [zip, folder];

    // Start render loop
    setProgressMsg(["Starting", null, null, null, null]);
    renderLoop(gl, program, buffer, uniformLocs, timeSeconds, frameIndex);
  }, [activeShaders, animateRange, fps, parsedShaders, shaderProps, size, tiling, renderLoop]);

  const stopRender = useCallback(() => {
    if(renderCallbackId.current !== null) {
      window.clearTimeout(renderCallbackId.current);
    }
    setProgressMsg(["Not Rendering", null, null, null, null]);
  }, []);

  return (
      <div className="render flex">
        <div className="renderBody">
          <BlankHeader onHome={props.onClose}>
            <button onClick={props.onExitRender}>Back To Editor</button>
            <button onClick={props.onClose}>Menu</button>
          </BlankHeader>
          <div className="renderCanvasContainer">
            <canvas
              width={size[0]}
              height={size[1]}
              ref={drawCanvas}
            />
          </div>
        </div>
        <div className="renderSidebar">
          <RenderOptions
            size={size} setSize={setSize}
            tiling={tiling} setTiling={setTiling}
            samples={samples} setSamples={setSamples}
            doAnimation={doAnimation} setDoAnimation={setDoAnimation}
            fps={fps} setFps={setFps}
            animateRange={animateRange} setAnimateRange={setAnimateRange}
            motionBlur={motionBlur} setMotionBlur={setMotionBlur}
            previewInterval={previewInterval} setPreviewInterval={setPreviewInterval}
            beginRender={beginRender}
            stopRender={stopRender}
          />
          <div>Status: {progressMsg[0]}</div>
          {progressMsg[1] !== null &&
            <div className="progressBarContainer">
              <div className="progressText">{(progressMsg[1] * 100).toFixed(1)} %</div>
              <div className="progressBar" style={{width: `${(progressMsg[1] * 100).toFixed(1)}%`}}></div>
            </div>
          }
          {progressMsg[2] !== null &&
            <div>
              {progressMsg[2]} / {totalFrames} Frames
            </div>
          }
          {progressMsg[3] !== null &&
            <div>
              {progressMsg[3]} / {samples} Samples
            </div>
          }
          {progressMsg[4] !== null &&
            <div>
              {progressMsg[4]} / {tiling * tiling} Tiles
            </div>
          }
          {resultFile.current !== null &&
            <button className="download" onClick={saveResultFile}>
              Download Images as ZIP
              {progressMsg[2] !== null && ` [${progressMsg[2] - 1} / ${totalFrames}]`}
            </button>
          }
        </div>
      </div>
  );
}
